package fr.labodoc.healthProfessional.webApp

import io.kvision.navigo.NavigateOptions
import io.kvision.navigo.Navigo

sealed interface Page {
  val url: String

  class Home(
    val popup: Popup? = null
  ) : Page {
    companion object {
      const val URL_PATTERN = "/"

      const val REGISTRATION_SUCCESSFUL_QUERY_KEY = "registrationSuccessful"
      const val REGISTRATION_CONFIRM_EMAIL_TOKEN_QUERY_KEY = "registrationConfirmEmailToken"
      const val APPLICATION_SUCCESSFUL_QUERY_KEY = "applicationSuccessful"
      const val APPLICATION_CONFIRM_EMAIL_TOKEN_QUERY_KEY = "applicationConfirmEmailToken"
      const val RESET_PASSWORD_TOKEN_QUERY_KEY = "resetPasswordToken"
    }

    sealed interface Popup {
      data class RegistrationSuccessful(val success: Boolean) : Popup
      data class RegistrationConfirmEmail(val token: String) : Popup
      data class ApplicationSuccessful(val success: Boolean) : Popup
      data class ApplicationConfirmEmail(val token: String) : Popup
      data class ResetPassword(val token: String) : Popup
    }

    override val url: String
      get() {
        return when (popup) {
          is Popup.RegistrationSuccessful -> "${URL_PATTERN}?${REGISTRATION_SUCCESSFUL_QUERY_KEY}=${popup.success}"
          is Popup.RegistrationConfirmEmail -> "${URL_PATTERN}?${REGISTRATION_CONFIRM_EMAIL_TOKEN_QUERY_KEY}=${popup.token}"
          is Popup.ApplicationSuccessful -> "${URL_PATTERN}?${APPLICATION_SUCCESSFUL_QUERY_KEY}=${popup.success}"
          is Popup.ApplicationConfirmEmail -> "${URL_PATTERN}?${APPLICATION_CONFIRM_EMAIL_TOKEN_QUERY_KEY}=${popup.token}"
          is Popup.ResetPassword -> "${URL_PATTERN}?${RESET_PASSWORD_TOKEN_QUERY_KEY}=${popup.token}"
          null -> URL_PATTERN
        }
      }
  }

  class Login(
    val redirectTo: String? = null
  ) : Page {
    companion object {
      const val URL_PATTERN = "/login"

      const val REDIRECT_TO_QUERY_KEY = "redirectTo"
    }

    override val url: String = "${URL_PATTERN}${redirectTo?.let { "?${REDIRECT_TO_QUERY_KEY}=$redirectTo" } ?: ""}"
  }

  class Logout : Page {
    companion object {
      const val URL_PATTERN = "/logout"
    }

    override val url: String = URL_PATTERN
  }

  class Register : Page {
    companion object {
      const val URL_PATTERN = "/register"
    }

    override val url: String = URL_PATTERN
  }

  class Contact : Page {
    companion object {
      const val URL_PATTERN = "/contact"
    }

    override val url: String = URL_PATTERN
  }

  class FAQ : Page {
    companion object {
      const val URL_PATTERN = "/faq"
    }

    override val url: String = URL_PATTERN
  }

  class About : Page {
    companion object {
      const val URL_PATTERN = "/about"
    }

    override val url: String = URL_PATTERN
  }

  class WhoAreWe : Page {
    companion object {
      const val URL_PATTERN = "/who-are-we"
    }

    override val url: String = URL_PATTERN
  }

  class PrivacyPolicy : Page {
    companion object {
      const val URL_PATTERN = "/privacy-policy"
    }

    override val url: String = URL_PATTERN
  }

  class CookiesPolicy : Page {
    companion object {
      const val URL_PATTERN = "https://labodoc-media.s3.gra.io.cloud.ovh.net/legal/cookies_policy.pdf"
    }

    override val url: String = URL_PATTERN
  }

  class LegalNotice : Page {
    companion object {
      const val URL_PATTERN = "https://cdn.labodoc.fr/legal/legal_notice.pdf"
    }

    override val url: String = URL_PATTERN
  }

  class TermsOfService : Page {
    companion object {
      const val URL_PATTERN = "https://cdn.labodoc.fr/legal/terms_of_service.pdf"
    }

    override val url: String = URL_PATTERN
  }

  class Definitions : Page {
    companion object {
      const val URL_PATTERN = "https://labodoc-media.s3.gra.io.cloud.ovh.net/legal/definitions.pdf"
    }

    override val url: String = URL_PATTERN
  }

  class Dashboard(
    val atcClassificationCode: String? = null
  ) : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/dashboard"

      const val ATC_CLASSIFICATION_CODE_QUERY_KEY = "atcClassificationCode"
    }

    override val url: String = "${URL_PATTERN}${atcClassificationCode?.let { "?${ATC_CLASSIFICATION_CODE_QUERY_KEY}=$atcClassificationCode" } ?: ""}"
  }

  class MedicineSheet(
    val medicineId: String
  ) : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/medicines/:id"
    }

    override val url: String = URL_PATTERN
      .replace(":id", medicineId)
  }

  class LaboratoriesList : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/laboratories"
    }

    override val url: String = URL_PATTERN
  }

  class LearnedSocietiesList : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/learned-societies"
    }

    override val url: String = URL_PATTERN
  }

  class ProfileInformation : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/profile/information"
    }

    override val url: String = URL_PATTERN
  }

  class ProfileMedicalInterests : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/profile/medical-interests"
    }

    override val url: String = URL_PATTERN
  }

  class ProfileNotificationsSettings : AuthenticatedPage {
    companion object {
      const val URL_PATTERN = "/profile/notifications-settings"
    }

    override val url: String = URL_PATTERN
  }
}

sealed interface AuthenticatedPage : Page

fun Navigo.navigate(to: Page, options: NavigateOptions) = navigate(to.url, options)
fun Navigo.navigate(to: Page) = navigate(to.url)
